/**
 * GPS-derived accel tier: per-lap relabel step for the lap-processing
 * pipeline. Extracted as a pure, mutating helper so it can be unit-tested
 * directly (the wiring in LapProcessingStage is a single call).
 *
 * When a lap has NO accelerometer (every acceleration point's source is
 * 'calculated-gps') and ≥2 GPS points, classify its GPS stream:
 *  - qualifies → promote every point to source 'gps-derived' and force
 *    speedTrendMismatch = false (validator exemption: the tier's signal IS the
 *    trend the validator compares against, so it cannot fail its own reference).
 *  - fails → relabel every point with the specific failing reason
 *    ('gps-too-sparse' | 'gps-too-noisy').
 * Any other lap (already sensor-backed, or degenerate <2 GPS points) is left
 * untouched and falls through to today's generic behaviour.
 */

import type { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import { classifyGpsAccelTier } from '../analysis/config/gpsDerivedAccel'
import {
  calculateSamplingFrequencyHz,
  getFrequencyAdaptiveConfig,
} from '../utils/timeUtils'

export function applyGpsDerivedTier(
  acceleration: AccelerationPoint[],
  gpsPoints: GpsPoint[]
): void {
  if (
    acceleration.length === 0 ||
    gpsPoints.length < 2 ||
    !acceleration.every((p) => p.source === 'calculated-gps')
  ) {
    return
  }

  const frequencyConfig = getFrequencyAdaptiveConfig(
    calculateSamplingFrequencyHz(gpsPoints)
  )
  const tier = classifyGpsAccelTier(gpsPoints, frequencyConfig)

  if (tier.qualifies) {
    for (const p of acceleration) {
      p.source = 'gps-derived'
      p.speedTrendMismatch = false
    }
  } else {
    for (const p of acceleration) {
      p.source = tier.reason
    }
  }
}
